import React from 'react';
import { motion } from 'framer-motion';
import { GiftIcon, CalendarIcon, ArrowTrendingUpIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const DividendOutlookCard = ({ dividendIncome, dividendSchedule, totalValue }) => {
  const dividendYield = totalValue > 0 ? (dividendIncome / totalValue) * 100 : 0;
  
  const getDividendLevel = (yield_pct) => {
    if (yield_pct >= 3.0) return { level: 'High', color: 'text-green-400', bgColor: 'bg-green-400/10' };
    if (yield_pct >= 1.5) return { level: 'Moderate', color: 'text-blue-400', bgColor: 'bg-blue-400/10' };
    if (yield_pct >= 0.5) return { level: 'Low', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' };
    return { level: 'Growth Focused', color: 'text-purple-400', bgColor: 'bg-purple-400/10' };
  };

  const dividendLevel = getDividendLevel(dividendYield);

  const projectedIncome = (years) => {
    const annualGrowth = 0.05; // Assume 5% annual dividend growth
    let totalIncome = 0;
    let currentIncome = dividendIncome;
    
    for (let year = 1; year <= years; year++) {
      totalIncome += currentIncome;
      currentIncome = currentIncome * (1 + annualGrowth);
    }
    
    return totalIncome;
  };

  const getMotivationalMessage = () => {
    if (dividendIncome > 1000) {
      return `Your portfolio is currently on track to generate $${dividendIncome.toLocaleString()} in passive income over the next year! This is real money that can accelerate your financial goals.`;
    } else if (dividendIncome > 100) {
      return `Your portfolio is building passive income of $${dividendIncome.toLocaleString()} annually. This income will grow over time as your investments compound.`;
    } else if (dividendIncome > 0) {
      return `Your portfolio is starting to generate passive income of $${dividendIncome.toLocaleString()} annually. Every dollar counts on your wealth-building journey!`;
    } else {
      return "Your portfolio is focused on growth rather than income. That works for long-term wealth building — add dividend-paying stocks when you want income diversity.";
    }
  };

  const formatUpcomingDividends = () => {
    if (!dividendSchedule || dividendSchedule.length === 0) {
      return [
        { date: '2024-01-15', symbol: 'AAPL', amount: 0.24, frequency: 'Quarterly' },
        { date: '2024-01-31', symbol: 'MSFT', amount: 0.75, frequency: 'Quarterly' },
        { date: '2024-02-15', symbol: 'JNJ', amount: 1.13, frequency: 'Quarterly' }
      ];
    }
    return dividendSchedule;
  };

  const upcomingDividends = formatUpcomingDividends();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <GiftIcon className="h-5 w-5 text-aurora-blue" />
          Dividend Outlook
        </h2>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${dividendLevel.bgColor} ${dividendLevel.color}`}>
          {dividendLevel.level} Yield
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dividend Summary */}
        <div className="space-y-4">
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">
                ${dividendIncome.toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">Estimated Annual Dividend Income</div>
              <div className={`text-lg font-semibold ${dividendLevel.color} mt-2`}>
                {dividendYield.toFixed(2)}% yield
              </div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-1">
                ${Math.round(projectedIncome(10)).toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">Projected 10-Year Income</div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <p className="text-white text-sm leading-relaxed">
              {getMotivationalMessage()}
            </p>
          </div>
        </div>

        {/* Upcoming Dividends */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-aurora-blue" />
            Upcoming Dividend Payments
          </h3>
          
          <div className="space-y-3">
            {upcomingDividends.slice(0, 6).map((dividend, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium text-sm">{dividend.symbol}</div>
                    <div className="text-gray-400 text-xs">{dividend.frequency}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-aurora-blue font-bold">${dividend.amount}</div>
                    <div className="text-gray-400 text-xs">{dividend.date}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {upcomingDividends.length > 6 && (
            <div className="text-center">
              <button className="text-aurora-blue hover:text-aurora-blue-dark text-sm font-medium transition-colors">
                View all upcoming dividends →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Income Growth Projection */}
      <div className="mt-8 border-t border-gray-700 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ArrowTrendingUpIcon className="h-5 w-5 text-aurora-blue" />
            Income Growth Projection
          </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-1">
                ${Math.round(projectedIncome(1)).toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">1-Year Income</div>
            </div>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-1">
                ${Math.round(projectedIncome(5)).toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">5-Year Income</div>
            </div>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-1">
                ${Math.round(projectedIncome(10)).toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">10-Year Income</div>
            </div>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-1">
                ${Math.round(projectedIncome(20)).toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">20-Year Income</div>
            </div>
          </div>
        </div>

        {/* Dividend Strategy Insights */}
        <div className="bg-gray-700 rounded-lg p-4">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <InformationCircleIcon className="h-4 w-4 text-aurora-blue" />
            Dividend Strategy Insights
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-300 mb-2">Income Focus (3%+ yield)</div>
              <div className="text-gray-400">High dividend stocks, REITs, utilities</div>
            </div>
            <div>
              <div className="text-gray-300 mb-2">Balanced (1.5-3% yield)</div>
              <div className="text-gray-400">Blue chip stocks, dividend growth funds</div>
            </div>
            <div>
              <div className="text-gray-300 mb-2">Growth Focus (0-1.5% yield)</div>
              <div className="text-gray-400">Growth stocks, tech companies</div>
            </div>
            <div>
              <div className="text-gray-300 mb-2">Your Portfolio</div>
              <div className={`font-medium ${dividendLevel.color}`}>
                {dividendYield.toFixed(2)}% - {dividendLevel.level}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DividendOutlookCard;
